// Thin fetch wrapper. The backend authenticates via __Host-dealflow-session
// (HttpOnly + Secure + SameSite=Strict). The browser sends it automatically
// for same-origin requests; we only need to forward the XSRF cookie value as
// a header on state-changing requests.
//
// The access cookie lives fifteen minutes; the refresh cookie two weeks. A
// 401 on any ordinary call is answered by one refresh and one retry, so an
// operator mid-review is not logged out because a quarter of an hour passed.
// Concurrent 401s share a single refresh, and the auth endpoints themselves
// are never refreshed (a failed login must stay a failed login).

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/**
 * The server's explanation for a failure, when it gave one.
 *
 * `ApiError.message` is only ever "HTTP <status>" — the useful text, where an
 * endpoint chooses to return one, is the `message` field of the JSON body. Most
 * endpoints deliberately return none, so a caller must always supply a fallback.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.body && typeof error.body === "object") {
    const message = (error.body as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return fallback;
}

function readCookie(name: string): string | null {
  const all = document.cookie ? document.cookie.split("; ") : [];
  for (const c of all) {
    const eq = c.indexOf("=");
    if (eq < 0) continue;
    if (c.slice(0, eq) === name) return decodeURIComponent(c.slice(eq + 1));
  }
  return null;
}

let refreshInFlight: Promise<boolean> | null = null;

/** One refresh for however many calls hit a 401 at the same moment. */
function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch("/api/v1/auth/refresh", { method: "POST", credentials: "same-origin" })
      .then((r) => r.ok, () => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function isAuthEndpoint(path: string): boolean {
  return path.startsWith("/api/v1/auth/");
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
  retried = false,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");

  const method = (init.method ?? "GET").toUpperCase();
  const stateChanging = method !== "GET" && method !== "HEAD";

  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (stateChanging) {
    const xsrf = readCookie("XSRF-TOKEN");
    if (xsrf) headers.set("X-XSRF-TOKEN", xsrf);
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: "same-origin",
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
  });

  if (response.status === 401 && !retried && !isAuthEndpoint(path) && (await refreshSession())) {
    return request<T>(path, init, true);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const body = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    throw new ApiError(response.status, `HTTP ${response.status}`, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, json?: unknown) => request<T>(path, { method: "POST", json }),
  put: <T>(path: string, json?: unknown) => request<T>(path, { method: "PUT", json }),
  patch: <T>(path: string, json?: unknown) => request<T>(path, { method: "PATCH", json }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  /**
   * Multipart upload. Deliberately no Content-Type header — the browser must
   * set it itself to include the form boundary. XSRF handling is shared with
   * every other state-changing request via {@link request}.
   */
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: "POST", body: form }),
};
